import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  DataTable,
  EmptyValue,
  Form,
  InputGroup,
  ListPage,
  PageHeader,
  RowActions,
  SegmentedControl,
  ServiceAreaPill,
  ACTIONS_COL_WIDTH,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import { usePermissions } from '../../../hooks/usePermissions';
import { areasService } from '../../../services/areasService';
import AttentionBadges from '../components/AttentionBadges';
import LocationsFilters, { type AreaOption, type ForemanAreaOption } from '../components/LocationsFilters';
import MaterialsSubnav from '../components/MaterialsSubnav';
import OwnerPill from '../components/OwnerPill';
import SiteBinsPanel from '../components/SiteBinsPanel';
import SiteDetailModal from '../components/SiteDetailModal';
import SiteFormModal from '../components/SiteFormModal';
import SiteMap from '../components/SiteMap';
import TypeMark from '../components/TypeMark';
import { getSites } from '../data';
import {
  attentionTotal,
  binCount,
  filterSites,
  isStockSite,
  locationCount,
  materialOptions,
  materialsStocked,
} from '../filters';
import type { Site, SiteFilters, SiteSort, SiteSortKey, SiteType, TypeFilter } from '../types';
import { EMPTY_FILTERS, TYPE_LABEL } from '../types';
import { googleMapsUrl } from '../utils/siteDrillThrough';
import { LOCATIONS_MANAGE_PERMISSION, STRUCTURE_NO_PERMISSION_REASON } from '../utils/structureForms';

type ViewMode = 'list' | 'map';

type LoadState = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; sites: Site[] };

const DEFAULT_SORT: SiteSort = { key: 'name', dir: 'asc' };

const SHORT_DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

function countOf(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Materials · Locations. Built the way QP Tracking is: one header, the shared filter card, one
 * table whose rows expand (here, to the bins under a site), and the full record opening in a
 * modal over the list rather than on a page of its own. A List / Map toggle, as on Work IDs,
 * shows the same filtered sites on a map.
 *
 * Rockfall routes hold no stock and are left off this page; they will get a page of their own.
 */
const LocationsPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const view: ViewMode = params.get('view') === 'map' ? 'map' : 'list';
  const openSiteId = params.get('site');

  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  // `?material=ID` arrives from the Catalogue's "N sites" link: the sites holding that material.
  const linkedMaterialId = params.get('material');
  const linkedFilters = (): SiteFilters => (linkedMaterialId ? { ...EMPTY_FILTERS, materialIds: [linkedMaterialId] } : EMPTY_FILTERS);
  const [pending, setPending] = useState<SiteFilters>(linkedFilters);
  const [applied, setApplied] = useState<SiteFilters>(linkedFilters);
  const [searchOpen, setSearchOpen] = useState(false);
  const [sort, setSort] = useState<SiteSort>(DEFAULT_SORT);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [serviceAreas, setServiceAreas] = useState<AreaOption[]>([]);
  const [foremanAreas, setForemanAreas] = useState<ForemanAreaOption[]>([]);
  const [addingSite, setAddingSite] = useState(false);
  const { hasPermission } = usePermissions();
  const canManageStructure = hasPermission(LOCATIONS_MANAGE_PERMISSION);

  const setParam = useCallback(
    (key: string, value: string | null) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );

  const loadSites = useCallback(async (quiet = false) => {
    if (!quiet) setLoad({ status: 'loading' });
    try {
      const sites = (await getSites()).filter(isStockSite);
      setLoad({ status: 'ready', sites });
    } catch (error) {
      if (!quiet) setLoad({ status: 'error', message: error instanceof Error ? error.message : 'Could not load sites.' });
    }
  }, []);

  useEffect(() => {
    void loadSites();
  }, [loadSites]);

  // Area names for the filters and the Foreman Area column. A failure only costs the names.
  useEffect(() => {
    let cancelled = false;
    Promise.all([areasService.getServiceAreas(), areasService.getForemanAreas()])
      .then(([services, foremen]) => {
        if (cancelled) return;
        setServiceAreas(services.map((area) => ({ id: String(area.id), name: area.name })));
        setForemanAreas(foremen.map((area) => ({ id: String(area.id), name: area.name, serviceAreaId: String(area.serviceAreaId) })));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const allSites = load.status === 'ready' ? load.sites : [];
  const foremanName = useMemo(() => {
    const names = new Map(foremanAreas.map((area) => [area.id, area.name]));
    return (id: string) => names.get(id) ?? `Foreman area ${id}`;
  }, [foremanAreas]);

  // Only offer areas this list can actually match.
  const siteServiceAreaIds = useMemo(() => new Set(allSites.map((site) => site.serviceAreaId)), [allSites]);
  const serviceOptions = useMemo(() => serviceAreas.filter((area) => siteServiceAreaIds.has(area.id)), [serviceAreas, siteServiceAreaIds]);
  const foremanOptions = useMemo(() => {
    const used = new Set(allSites.flatMap((site) => site.foremanAreaIds));
    return foremanAreas.filter((area) => used.has(area.id));
  }, [foremanAreas, allSites]);
  const materials = useMemo(() => materialOptions(allSites), [allSites]);

  const effective = useMemo(() => ({ ...applied, search: pending.search }), [applied, pending.search]);
  const sites = useMemo(() => filterSites(allSites, effective, sort, foremanName), [allSites, effective, sort, foremanName]);
  const visibleIds = useMemo(() => new Set(sites.map((site) => site.id)), [sites]);

  const totals = useMemo(
    () => ({
      bins: sites.reduce((sum, site) => sum + binCount(site), 0),
      needAction: sites.filter((site) => attentionTotal(site) > 0).length,
    }),
    [sites]
  );

  const openSite = (site: Site) => setParam('site', site.id);
  const toggleExpanded = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const columns: DataTableColumn<Site>[] = [
    {
      key: 'name',
      header: 'Site',
      sortable: true,
      pin: 'left',
      mobile: 'title',
      className: 'tw-min-w-[14rem]',
      cell: (site) => (
        <div className="tw-min-w-0">
          <p className="tw-mb-0 tw-flex tw-items-center tw-gap-2 tw-font-semibold tw-text-content">
            <TypeMark type={site.type} />
            <span className="tw-truncate">{site.name}</span>
            {site.number ? <span className="tw-shrink-0 tw-text-xs tw-font-normal tw-text-content-muted">#{site.number}</span> : null}
          </p>
          {site.description ? (
            <p className="tw-mb-0 tw-line-clamp-1 tw-text-xs tw-text-content-muted" title={site.description}>
              {site.description}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      mobile: 'meta',
      cell: (site) => TYPE_LABEL[site.type],
    },
    {
      key: 'attention',
      header: 'Needs action',
      sortable: true,
      mobileLabel: 'Needs action',
      className: 'tw-min-w-[9rem]',
      cell: (site) => <AttentionBadges attention={site.attention} />,
    },
    {
      key: 'owner',
      header: 'Owner',
      sortable: true,
      mobileLabel: 'Owner',
      cell: (site) => <OwnerPill owner={site.owner} />,
    },
    {
      key: 'serviceArea',
      header: 'Service area',
      sortable: true,
      mobile: 'hidden',
      cell: (site) => <ServiceAreaPill value={site.serviceAreaName} compact />,
    },
    {
      key: 'foremanArea',
      header: 'Foreman area',
      sortable: true,
      mobileLabel: 'Foreman area',
      className: 'tw-min-w-[9rem]',
      cell: (site) =>
        site.foremanAreaIds.length ? (
          <span className="tw-line-clamp-2">{site.foremanAreaIds.map(foremanName).join(', ')}</span>
        ) : (
          <EmptyValue />
        ),
    },
    {
      key: 'locations',
      header: 'Locations',
      sortable: true,
      align: 'right',
      mobile: 'hidden',
      className: 'tw-tabular-nums',
      cell: (site) => locationCount(site),
    },
    {
      key: 'bins',
      header: 'Bins',
      sortable: true,
      align: 'right',
      mobileLabel: 'Bins',
      className: 'tw-tabular-nums',
      cell: (site) => binCount(site),
    },
    {
      key: 'materials',
      header: 'Materials',
      sortable: true,
      align: 'right',
      mobile: 'hidden',
      className: 'tw-tabular-nums',
      cell: (site) => materialsStocked(site).length,
    },
    {
      key: 'lastCount',
      header: 'Last count',
      sortable: true,
      mobile: 'hidden',
      className: 'tw-whitespace-nowrap',
      cell: (site) => (site.lastCountAt ? SHORT_DATE.format(new Date(site.lastCountAt)) : <EmptyValue />),
    },
    {
      key: 'actions',
      header: '',
      pin: 'right',
      align: 'right',
      mobile: 'trailing',
      width: `calc(${ACTIONS_COL_WIDTH} + 2.5rem)`,
      cell: (site) => {
        const open = expanded.has(site.id);
        return (
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-1" onClick={(event) => event.stopPropagation()}>
            <Button
              variant="outline-primary"
              size="sm"
              active={open}
              aria-expanded={open}
              aria-label={`${open ? 'Hide' : 'Show'} the bins at ${site.name}`}
              disabled={binCount(site) === 0}
              disabledReason={binCount(site) === 0 ? 'No bins at this site yet' : undefined}
              onClick={() => toggleExpanded(site.id)}
            >
              <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} aria-hidden="true" />
            </Button>
            <RowActions
              actions={[
                { kind: 'view', label: `Open ${site.name}`, onClick: () => openSite(site) },
                {
                  kind: 'custom',
                  icon: 'bi-geo-alt',
                  label:
                    site.latitude != null && site.longitude != null
                      ? 'Open in Google Maps'
                      : 'Open in Google Maps — no position recorded for this site',
                  disabled: site.latitude == null || site.longitude == null,
                  onClick: () =>
                    site.latitude != null && site.longitude != null
                      ? window.open(googleMapsUrl(site.latitude, site.longitude), '_blank', 'noopener')
                      : undefined,
                },
              ]}
            />
          </div>
        );
      },
    },
  ];

  const legendCounts = useMemo(() => {
    const counts: Record<SiteType, number> = { pit: 0, yard: 0, rockfall: 0 };
    for (const site of sites) counts[site.type] += 1;
    return counts;
  }, [sites]);
  // The map's legend toggles the Type filter, applied straight away like a tab.
  const mapType: TypeFilter = applied.types.length === 1 ? applied.types[0] : 'all';
  const setMapType = (type: TypeFilter) => {
    const next = { ...applied, types: type === 'all' || type === 'rockfall' ? [] : [type] };
    setApplied(next);
    setPending((current) => ({ ...current, types: next.types }));
  };

  const filterKey = JSON.stringify(effective);

  return (
    <ListPage>
      <PageHeader
        title="Locations"
        subtitle="Sites, the storage locations inside them, and the bins that hold the material."
        viewToggle={
          <SegmentedControl
            aria-label="Locations display"
            value={view}
            onChange={(value) => setParam('view', value === 'map' ? 'map' : null)}
            options={[
              { value: 'list', label: 'List', icon: 'bi-list-ul' },
              { value: 'map', label: 'Map', icon: 'bi-map' },
            ]}
          />
        }
        actions={[
          { key: 'refresh', label: 'Refresh', icon: 'bi-arrow-clockwise', intent: 'neutral', overflow: true, onClick: () => void loadSites() },
          {
            key: 'search',
            label: searchOpen ? 'Close Search' : 'Search',
            icon: searchOpen ? 'bi-x-lg' : 'bi-search',
            intent: 'secondary',
            active: searchOpen || !!pending.search,
            onClick: () => {
              if (searchOpen) setPending((current) => ({ ...current, search: '' }));
              setSearchOpen(!searchOpen);
            },
          },
          {
            key: 'add-site',
            label: 'Add site',
            icon: 'bi-plus-lg',
            intent: 'primary',
            disabled: !canManageStructure,
            title: canManageStructure ? undefined : STRUCTURE_NO_PERMISSION_REASON,
            onClick: () => setAddingSite(true),
          },
        ]}
      />

      <MaterialsSubnav />

      <LocationsFilters
        pending={pending}
        applied={applied}
        onChange={setPending}
        onApply={() => setApplied({ ...pending })}
        onClear={() => {
          setPending({ ...EMPTY_FILTERS, search: pending.search });
          setApplied(EMPTY_FILTERS);
        }}
        serviceAreas={serviceOptions}
        foremanAreas={foremanOptions}
        materials={materials}
      />

      {searchOpen ? (
        <InputGroup className="tw-mb-3">
          <InputGroup.Text>
            <i className="bi bi-search" aria-hidden="true" />
          </InputGroup.Text>
          <Form.Control
            autoFocus
            aria-label="Search sites"
            placeholder="Site name, number, address or service area"
            value={pending.search}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPending((current) => ({ ...current, search: event.target.value }))}
          />
          {pending.search ? (
            <Button variant="outline-secondary" aria-label="Clear search" onClick={() => setPending((current) => ({ ...current, search: '' }))}>
              <i className="bi bi-x-lg" aria-hidden="true" />
            </Button>
          ) : null}
        </InputGroup>
      ) : null}

      {linkedMaterialId && applied.materialIds.includes(linkedMaterialId) ? (
        // LIST-03: say what the link filtered to, and let the user drop it.
        <div className="tw-mb-2">
          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-line-input tw-bg-surface tw-py-0.5 tw-pl-3 tw-pr-1 tw-text-sm">
            Showing: {materials.find((m) => m.id === linkedMaterialId)?.label ?? 'one material'}
            <button
              type="button"
              className="tw-inline-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full hover:tw-bg-surface-hover"
              aria-label="Show every site"
              onClick={() => {
                setParam('material', null);
                setPending((current) => ({ ...current, materialIds: current.materialIds.filter((id) => id !== linkedMaterialId) }));
                setApplied((current) => ({ ...current, materialIds: current.materialIds.filter((id) => id !== linkedMaterialId) }));
              }}
            >
              <i className="bi bi-x" aria-hidden="true" />
            </button>
          </span>
        </div>
      ) : null}

      {load.status === 'ready' ? (
        <p className="tw-mb-2 tw-text-sm tw-text-content-muted" aria-live="polite">
          {countOf(sites.length, 'site', 'sites')} · {countOf(totals.bins, 'bin', 'bins')}
          {totals.needAction ? (
            <>
              {' · '}
              <span className="tw-font-medium tw-text-danger">{countOf(totals.needAction, 'site needs', 'sites need')} action</span>
            </>
          ) : null}
        </p>
      ) : null}

      <ListPage.Body>
        {load.status === 'error' ? (
          <Alert variant="danger">
            <p className="tw-mb-2">{load.message}</p>
            <Button size="sm" variant="outline-danger" onClick={() => void loadSites()}>
              Try again
            </Button>
          </Alert>
        ) : view === 'map' ? (
          <div className="tw-h-full tw-min-h-[24rem] tw-overflow-hidden tw-rounded-lg tw-border tw-border-line-input">
            <SiteMap
              sites={sites}
              visibleIds={visibleIds}
              selectedId={openSiteId}
              hoverId={hoverId}
              filterKey={filterKey}
              layoutKey={view}
              type={mapType}
              legendCounts={legendCounts}
              onSelect={(id) => setParam('site', id)}
              onHover={setHoverId}
              onClearSelection={() => undefined}
              onType={setMapType}
            />
          </div>
        ) : (
          <DataTable
            fill
            columns={columns}
            data={sites}
            rowKey={(site) => site.id}
            loading={load.status === 'loading'}
            onRowClick={openSite}
            sortBy={sort.key}
            sortDir={sort.dir}
            onSort={(key, dir) => setSort({ key: key as SiteSortKey, dir })}
            isExpanded={(site) => expanded.has(site.id)}
            renderExpanded={(site) => <SiteBinsPanel site={site} filters={applied} onOpen={() => openSite(site)} />}
            emptyMessage={
              allSites.length === 0 ? 'No sites with stock in your service area yet.' : 'No sites match these filters.'
            }
          />
        )}
      </ListPage.Body>

      <SiteDetailModal
        siteId={openSiteId}
        onHide={() => setParam('site', null)}
        onChanged={() => void loadSites(true)}
      />
      {canManageStructure ? (
        <SiteFormModal
          show={addingSite}
          site={null}
          onHide={() => setAddingSite(false)}
          // Open the new site straight away: its locations and bins are added from there.
          onSaved={(saved) => {
            setAddingSite(false);
            void loadSites(true);
            setParam('site', saved.id);
          }}
        />
      ) : null}
    </ListPage>
  );
};

export default LocationsPage;
